import { cloneElement, memo, useRef, type CSSProperties, type ReactNode } from "react";
import { brand, colors, scaleOf } from "../../config/theme";

export const MARKER_SIZE = 32;
/** Lado del sprite cenital. Va más grande que el glifo porque dibuja un vehículo, no una figura. */
export const MARKER_IMAGE_SIZE = 46;
/** Misma duración que la interpolación de posición de `movimientoSuave`, para que giro y avance vayan juntos. */
export const MARKER_ROTATION_MS = 700;
export const MARKER_STROKE = "#FFFFFF";
export const CLUSTER_SIZE = 40;

export const MARKER_FALLBACK_COLOR = colors.light.text.muted;

export const VEHICLE_STATUS_COLORS: Record<string, string> = {
  driving: brand.primary,
  sleep: scaleOf(colors.light, "info")["500"],
  stopped: scaleOf(colors.light, "error")["500"],
  offline: MARKER_FALLBACK_COLOR,
  ralenti: scaleOf(colors.light, "warning")["500"],
};

export const vehicleStatusColor = (status: string | null | undefined): string =>
  VEHICLE_STATUS_COLORS[(status ?? "").toString()] ?? MARKER_FALLBACK_COLOR;

export const dominantStatusColor = (statuses: string[]): string => {
  if (statuses.length === 0) return MARKER_FALLBACK_COLOR;

  const counts = statuses.reduce<Record<string, number>>((acc, status) => {
    const key = status || "offline";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  return vehicleStatusColor(dominant);
};

export interface MarkerVisualFields {
  id: string;
  latitude: number;
  longitude: number;
  status: string;
  course?: number | null;
  icon?: string | null;
}

export const markerVisualKey = (vehicle: MarkerVisualFields): string =>
  [
    vehicle.id,
    vehicle.latitude,
    vehicle.longitude,
    vehicle.status,
    vehicle.course ?? "",
    vehicle.icon ?? "",
  ].join("|");

export const statusesVisualKey = (
  vehicles: readonly { status: string }[] | null | undefined,
): string => (vehicles ?? []).map((vehicle) => vehicle.status ?? "").join(",");

export type MarkerGlyphId =
  | "driving"
  | "ralenti"
  | "stopped"
  | "offline"
  | "plain";

const MARKER_GLYPH_BY_STATUS: Record<string, MarkerGlyphId> = {
  driving: "driving",
  idle: "ralenti",
  ralenti: "ralenti",
  stopped: "stopped",
  offline: "offline",
  sleep: "plain",
};

const MARKER_GLYPHS: Record<MarkerGlyphId, (color: string) => ReactNode> = {
  driving: (color) => (
    <path
      d="M100,12 L164,152 Q167,159 159,156 L104,133 Q100,131 96,133 L41,156 Q33,159 36,152 Z"
      fill={color}
      stroke={MARKER_STROKE}
      strokeWidth={9}
      strokeLinejoin="round"
      paintOrder="stroke"
    />
  ),
  ralenti: (color) => (
    <circle
      cx={100}
      cy={100}
      r={76}
      fill={color}
      stroke={MARKER_STROKE}
      strokeWidth={10}
      paintOrder="stroke"
    />
  ),
  stopped: (color) => (
    <rect
      x={30}
      y={30}
      width={140}
      height={140}
      rx={34}
      fill={color}
      stroke={MARKER_STROKE}
      strokeWidth={10}
      paintOrder="stroke"
    />
  ),
  offline: (color) => (
    <path
      d="M100,18 L171,59 L171,141 L100,182 L29,141 L29,59 Z"
      fill={color}
      stroke={MARKER_STROKE}
      strokeWidth={10}
      strokeLinejoin="round"
      paintOrder="stroke"
    />
  ),
  plain: (color) => (
    <path
      d="M100,16 L184,100 L100,184 L16,100 Z"
      fill={color}
      stroke={MARKER_STROKE}
      strokeWidth={10}
      strokeLinejoin="round"
      paintOrder="stroke"
    />
  ),
};

export interface MarkerShape {
  glyph: MarkerGlyphId;
  rotation: number | null;
}

export const getMarkerShape = (
  status: string | null | undefined,
  course: number | null | undefined,
): MarkerShape => {
  const glyph = MARKER_GLYPH_BY_STATUS[(status ?? "").toString()] ?? "plain";
  const heading = course != null ? Number(course) : null;
  const hasHeading = heading != null && !Number.isNaN(heading) && heading !== 0;

  if (glyph !== "driving") return { glyph, rotation: null };
  return hasHeading
    ? { glyph, rotation: heading }
    : { glyph: "plain", rotation: null };
};

export const VehicleMarkerGlyph = memo(
  ({
    glyph,
    color,
    size,
    rotation,
  }: {
    glyph: MarkerGlyphId;
    color: string;
    size: number;
    rotation: number | null;
  }) => (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      style={{
        display: "block",
        transform: rotation != null ? `rotate(${rotation}deg)` : undefined,
        filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
      }}
    >
      {MARKER_GLYPHS[glyph](color)}
    </svg>
  ),
);

/**
 * Giro por el camino más corto: de 350° a 10° son 20° a la derecha, no 340° a
 * la izquierda. Devuelve el delta con signo en (-180, 180].
 */
export const shortestDelta = (from: number, to: number): number =>
  ((to - from + 540) % 360) - 180;

/**
 * Ángulo "desenrollado" para animar con CSS. `rotate()` interpola el número
 * literal, así que hay que acumular (…, 350°, 370°) en vez de saltar a 10°, o
 * la unidad pega un giro completo cada vez que cruza el norte.
 */
const useSmoothHeading = (heading: number): number => {
  const acumulado = useRef(heading);
  acumulado.current += shortestDelta(acumulado.current, heading);
  return acumulado.current;
};

/** Rumbo a usar en un puntero con imagen. Un sprite siempre apunta a algún lado: sin dato, al norte. */
export const imageMarkerHeading = (course: number | null | undefined): number => {
  const heading = course != null ? Number(course) : 0;
  return Number.isFinite(heading) ? heading : 0;
};

/**
 * Geometría del puntero con imagen, siguiendo `marker-prototype.html`.
 *
 * El sprite es un lienzo CUADRADO con el vehículo centrado ocupando el 92% del
 * alto (así lo deja `scripts/punteros-unidades.cjs`). Para que la cápsula del
 * estado abrace la silueta —y no quede un óvalo suelto alrededor— hace falta
 * saber qué parte de ese cuadrado es vehículo: eso es `aspect`, el alto entre
 * el ancho, que el script mide al subir y guarda en el icono.
 */
const RELLENO_SPRITE = 0.92;
/**
 * Aire alrededor del vehículo, en proporción a su ALTO (del boceto: 8 px sobre
 * 91,6). Va contra el alto y no contra el ancho a propósito: el alto lo fija el
 * zoom y es igual para todas las unidades, así que el margen se ve uniforme en
 * toda la flota y sólo el ANCHO de la cápsula cambia según el vehículo. Atado
 * al ancho, una moto quedaba además más corta que un camión de la misma
 * longitud en pantalla.
 */
const AIRE = 0.0873;
/** Proporción de un sedán. Sólo se usa si el icono no trae la suya medida. */
export const ASPECT_POR_DEFECTO = 2.12;

/*
  La escala —metros por píxel, piso de legibilidad, amortiguación al acercar—
  vive en `escalaDelPuntero.ts`: es aritmética pura y así se puede probar sin
  arrastrar el tema ni Firebase. Se reexporta para no mover a quien ya la
  importaba desde aquí.
*/
export {
  metrosPorPixel,
  alturaPorZoom,
  LARGO_POR_DEFECTO_M,
  ZOOM_TAMANO_REAL,
} from "./escalaDelPuntero";

/**
 * Cuántas ondas lanza cada estado y cada cuánto.
 *
 * Conduciendo va más rápido y con más ondas; detenida, más lento y con menos.
 * El ritmo es la lectura: no hace falta mirar el color para saber si se mueve.
 *
 * «Sin señal» no lanza ninguna, y es lo correcto: una onda dice «esto está
 * pasando ahora», y de una unidad sin señal lo único que se sabe es que no se
 * sabe nada. Ahí la selección se marca con la misma figura, quieta.
 */
const ONDAS_POR_ESTADO: Record<string, { cuantas: number; duracion: number }> = {
  driving: { cuantas: 3, duracion: 4.2 },
  ralenti: { cuantas: 3, duracion: 5.4 },
  idle: { cuantas: 3, duracion: 5.4 },
  stopped: { cuantas: 2, duracion: 6.6 },
  sleep: { cuantas: 2, duracion: 6.6 },
  offline: { cuantas: 0, duracion: 0 },
};

/**
 * Las ondas que salen del vehículo cuando la unidad está destacada.
 *
 * Van en SVG y no en divs porque conduciendo la figura no es una cápsula: su
 * frente termina en punta, y esa punta —la flecha de rumbo— se expande con la
 * onda en vez de ir pegada aparte. Un `border-radius` no dibuja eso.
 */
const OndasDelEstado = memo(
  ({
    color,
    status,
    ancho,
    alto,
    caja,
    conPunta,
  }: {
    color: string;
    status: string | null | undefined;
    ancho: number;
    alto: number;
    caja: number;
    conPunta: boolean;
  }) => {
    const { cuantas, duracion } = ONDAS_POR_ESTADO[(status ?? "").toString()] ?? {
      cuantas: 2,
      duracion: 6.6,
    };

    const r = ancho / 2;
    const punta = alto * 0.18;
    // Cápsula normal, o cápsula con el frente en punta al conducir.
    const figura = conPunta ? (
      <path
        d={`M0 ${-alto / 2 - punta}
            C${r * 0.3} ${-alto / 2 - punta * 0.55} ${r} ${-alto / 2 + r * 0.25} ${r} ${-alto / 2 + r}
            L${r} ${alto / 2 - r}
            A${r} ${r} 0 0 1 ${-r} ${alto / 2 - r}
            L${-r} ${-alto / 2 + r}
            C${-r} ${-alto / 2 + r * 0.25} ${-r * 0.3} ${-alto / 2 - punta * 0.55} 0 ${-alto / 2 - punta} Z`}
        strokeLinejoin="round"
      />
    ) : (
      <rect x={-ancho / 2} y={-alto / 2} width={ancho} height={alto} rx={r} />
    );

    // Sin señal: la misma figura, quieta, para que seleccionarla se vea.
    const repeticiones = cuantas || 1;

    return (
      <svg
        className="absolute overflow-visible pointer-events-none"
        width={caja}
        height={caja}
        viewBox={`${-caja / 2} ${-caja / 2} ${caja} ${caja}`}
      >
        <g fill={color} stroke={color}>
          {Array.from({ length: repeticiones }, (_, i) =>
            cloneElement(figura, {
              key: i,
              className: cuantas ? "vm-wave" : undefined,
              // Escalonadas: cada una arranca en un momento distinto del ciclo,
              // así la que sale no espera a que la anterior se haya disuelto.
              style: cuantas
                ? ({
                    "--vm-wave-dur": `${duracion}s`,
                    "--vm-wave-delay": `${-(duracion / cuantas) * i}s`,
                  } as CSSProperties)
                : { fillOpacity: 0.25, strokeWidth: 2, opacity: 0.6 },
            }),
          )}
        </g>
      </svg>
    );
  },
);

/**
 * Puntero con la imagen del vehículo (sprite cenital del catálogo de iconos).
 *
 * Capas, de atrás hacia adelante: cápsula del color del estado, contorno de
 * selección, cono de rumbo (solo conduciendo) y el sprite. Las cuatro rotan
 * juntas; la etiqueta con el nombre de la unidad vive fuera de este componente
 * justamente para que no gire.
 */
export const VehicleImageMarker = memo(
  ({
    src,
    color,
    heading,
    status,
    aspect = ASPECT_POR_DEFECTO,
    altoVehiculo = 42,
    showCone = false,
    selected = false,
    onError,
  }: {
    src: string;
    color: string;
    heading: number;
    status: string | null | undefined;
    /** Alto entre ancho del vehículo. Lo mide el script al subir el sprite. */
    aspect?: number | null;
    /** Alto del vehículo en pantalla. Lo decide el zoom del mapa. */
    altoVehiculo?: number;
    showCone?: boolean;
    /**
     * La unidad está destacada: seleccionada, recién clicada, o entre los
     * resultados de un filtro. Es lo ÚNICO que saca la cápsula de color.
     */
    selected?: boolean;
    onError?: () => void;
  }) => {
    const angulo = useSmoothHeading(heading);
    const apagada = (status ?? "") === "offline";

    const proporcion = aspect && aspect > 0 ? aspect : ASPECT_POR_DEFECTO;
    const anchoVehiculo = altoVehiculo / proporcion;
    const aire = altoVehiculo * AIRE;
    const capsula = { ancho: anchoVehiculo + aire * 2, alto: altoVehiculo + aire * 2 };
    // El sprite se pinta al tamaño del lienzo completo, no del vehículo: dentro
    // de ese cuadrado el vehículo ya viene centrado y al 92%.
    const lienzo = altoVehiculo / RELLENO_SPRITE;
    // Cuadrado que contiene la cápsula en CUALQUIER ángulo, para que la
    // etiqueta de la unidad nunca se monte encima al girar.
    const caja = capsula.alto + (selected ? capsula.ancho * 0.28 : 0);

    return (
      <div
        className="relative flex items-center justify-center pointer-events-none"
        style={{
          width: caja,
          height: caja,
          transform: `rotate(${angulo}deg)`,
          transition: `transform ${MARKER_ROTATION_MS}ms linear`,
        }}
      >
        {/*
          El color del estado NO está siempre. En reposo el mapa enseña sólo el
          vehículo —como Uber—, y con veinte unidades a la vista veinte figuras
          de colores compiten entre ellas sin que ninguna signifique nada.
          Aparece cuando la unidad está destacada: seleccionada, clicada o
          dentro de un filtro. Ahí sí hay una pregunta que contestar, «¿cómo va
          ésta?», y el color la contesta.

          Tampoco lleva un aro aparte para la selección: un segundo anillo mete
          dos figuras concéntricas en un marcador de 40 px y la de fuera tapa a
          la de dentro. La onda dice las dos cosas a la vez.
        */}
        {selected && (
          <OndasDelEstado
            color={color}
            status={status}
            ancho={capsula.ancho}
            alto={capsula.alto}
            caja={caja}
            conPunta={showCone}
          />
        )}
        <img
          src={src}
          alt=""
          onError={onError}
          className="relative block object-contain"
          style={{
            width: lienzo,
            height: lienzo,
            filter: apagada
              ? "grayscale(1) opacity(0.45)"
              : "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35))",
          }}
        />
      </div>
    );
  },
);

export const getClusterCoreSize = (count: number) =>
  count < 10 ? 36 : count < 100 ? 44 : 52;

export const getClusterFontSize = (count: number) =>
  count < 10 ? 14 : count < 100 ? 15 : 16;

/**
 * El cúmulo: cuántas unidades hay apiladas, y cómo están.
 *
 * ── Dos intentos antes de este ──────────────────────────────────────────────
 * Primero eran tres círculos con halos difusos: sobre el mapa oscuro se leían
 * como una mancha, y es el aspecto por omisión de cualquier librería de
 * clustering. Después probé un hexágono con corchetes de puntería: demasiado
 * videojuego, y la forma no aportaba nada que el número no dijera ya.
 *
 * ── Qué hace este ──────────────────────────────────────────────────────────
 * El problema de fondo no era la forma: era que un «10» no dice NADA sobre
 * esas diez unidades. Había que abrir el cúmulo para saber si estaban bien.
 *
 * Ahora el anillo lo cuenta: cada arco es un estado, y su largo es cuántas
 * unidades hay en él. Un anillo entero del azul de conduciendo se lee «las
 * diez van en marcha»; uno partido en gris y rojo se lee «la mitad no reporta
 * y hay paradas» — sin tocar nada, desde la distancia a la que se mira un
 * mapa. El número sigue en el centro para quien quiera la cifra exacta.
 *
 * La forma es un círculo limpio, no una figura técnica: la elegancia está en
 * que el adorno se haya convertido en dato.
 */
export const ClusterBubble = ({
  count,
  color,
  mezcla,
  enfocado = false,
}: {
  count: number;
  color: string;
  /** Cuántas unidades hay por estado. Sin esto, el anillo cae a un solo arco. */
  mezcla?: Record<string, number>;
  enfocado?: boolean;
}) => {
  const coreSize = getClusterCoreSize(count);
  const anillo = coreSize + 10;

  /*
    Los arcos. Se dibujan sobre un círculo con `stroke-dasharray`: cada tramo
    ocupa su fracción del perímetro y el siguiente arranca donde acabó el
    anterior. Un hueco de 2 unidades los separa para que dos estados contiguos
    no se lean como uno solo.
  */
  const R = 46;
  const PERIMETRO = 2 * Math.PI * R;
  const entradas = Object.entries(mezcla ?? {}).filter(([, n]) => n > 0);
  const total = entradas.reduce((a, [, n]) => a + n, 0) || count || 1;
  const HUECO = entradas.length > 1 ? 2 : 0;

  let recorrido = 0;
  const arcos = entradas.map(([estado, n]) => {
    const largo = Math.max((n / total) * PERIMETRO - HUECO, 1);
    const arco = {
      estado,
      color: vehicleStatusColor(estado),
      dash: `${largo} ${PERIMETRO - largo}`,
      desfase: -recorrido,
    };
    recorrido += largo + HUECO;
    return arco;
  });

  return (
    <>
      <svg
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        width={anillo}
        height={anillo}
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true"
      >
        {/* La pista: sin ella, un cúmulo de un solo estado parece un anillo roto. */}
        <circle
          cx="50"
          cy="50"
          r={R}
          stroke={color}
          strokeWidth={6}
          opacity={0.18}
        />
        {arcos.map((a) => (
          <circle
            key={a.estado}
            cx="50"
            cy="50"
            r={R}
            stroke={a.color}
            strokeWidth={6}
            strokeLinecap="butt"
            strokeDasharray={a.dash}
            strokeDashoffset={a.desfase}
            /* Desde arriba, como un reloj: es de donde el ojo espera que salga. */
            transform="rotate(-90 50 50)"
          />
        ))}
      </svg>

      <div
        className="relative shrink-0 flex items-center justify-center rounded-full font-semibold leading-none"
        style={{
          width: `${coreSize}px`,
          height: `${coreSize}px`,
          backgroundColor: MARKER_STROKE,
          color: color,
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontVariantNumeric: "tabular-nums",
          fontSize: `${getClusterFontSize(count)}px`,
          boxShadow: enfocado
            ? `0 0 0 2px ${color}, 0 4px 14px rgba(0,0,0,0.35)`
            : "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        {count}
      </div>
    </>
  );
};
